<template>
	<v-dialog
		:model-value="modelValue"
		width="32%"
		max-width="500px"
		persistent
		@update:model-value="(val) => $emit('update:modelValue', val)"
	>
		<v-card color="rgba(41, 41, 47, 0.8)">
			<v-card-title>{{ $t("EXPORT_AS") }}</v-card-title>
			<v-card-text>
				<v-card-title
					class="justify-center text-center"
					style="word-break: break-word; white-space: break-spaces"
				>
					{{ $t("ExportObject", { name: obj?.name ?? "" }) }}<br />
					{{ $t("SelectOutputFormat") }}
				</v-card-title>

				<v-card-subtitle class="pa-2 mt-2">{{
					$t("OUTPUT_FORMAT")
				}}</v-card-subtitle>
				<div class="format-checkboxes">
					<v-checkbox
						v-model="formats.landxml"
						:label="$t('LandXML')"
						hide-details
						density="compact"
					/>
					<v-checkbox
						v-model="formats.dxf"
						:label="$t('DXF')"
						hide-details
						density="compact"
					/>
				</div>

				<v-card-subtitle class="pa-2 mt-2">{{
					$t("OUTPUT_DESTINATION")
				}}</v-card-subtitle>
				<v-radio-group v-model="destination">
					<v-radio :label="$t('SAVE_TO_LOCAL')" value="local" />
					<v-radio
						:label="$t('Registertodashboard')"
						value="dashboard"
					/>
				</v-radio-group>

				<v-row class="ma-0">
					<v-col>
						<v-card-actions class="justify-end">
							<v-btn variant="flat" color="red" @click="closeDialog">
								{{ $t("CANCEL") }}
							</v-btn>
							<v-btn
								variant="flat"
								color="primary"
								:disabled="!canExport"
								@click="onClickExport"
							>
								{{ $t("EXPORT_AS") }}
							</v-btn>
						</v-card-actions>
					</v-col>
				</v-row>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script lang="ts">
import type { FoundationExcavationObject } from "@/models/objects/FoundationExcavationObject";
import { ObjectType } from "@/models/objects/types";
import { distanceUnits } from "@/models/siteUnit.types";
import { fetchObjectDxfBlob } from "@/utils/dxfExportApi";
import { uploadDesignFileToDashboard } from "@/utils/foundationExcavationApiUtils";
import {
	handleCustomError,
	handleSingleUploadSuccess,
} from "@/utils/notificationUtils";
import { LandXML } from "earthbrain-viewer";
import { type PropType, defineComponent } from "vue";

type MeshGeometry = {
	vertices: number[];
	indices: number[];
};

type ExportFormats = {
	landxml: boolean;
	dxf: boolean;
};

type ExportDestination = "local" | "dashboard";

const DEFAULT_FORMATS = (): ExportFormats => ({
	landxml: true,
	dxf: false,
});

const DEFAULT_DESTINATION: ExportDestination = "local";

function pad2(n: number): string {
	return n.toString().padStart(2, "0");
}

export default defineComponent({
	name: "ExportFoundationExcavationDialog",
	props: {
		obj: {
			type: Object as PropType<FoundationExcavationObject>,
			required: true,
		},
		modelValue: {
			type: Boolean,
			default: false,
		},
	},
	emits: ["update:modelValue"],
	data() {
		return {
			formats: DEFAULT_FORMATS() as ExportFormats,
			destination: DEFAULT_DESTINATION as ExportDestination,
		};
	},
	computed: {
		canExport(): boolean {
			return Object.values(this.formats).some((v) => v);
		},
	},
	watch: {
		modelValue(newVal: boolean, oldVal: boolean) {
			if (oldVal && !newVal) {
				this.resetState();
			}
		},
	},
	methods: {
		resetState() {
			this.formats = DEFAULT_FORMATS();
			this.destination = DEFAULT_DESTINATION;
		},
		closeDialog() {
			this.$emit("update:modelValue", false);
		},
		createFileName(date: Date, objName: string, siteName: string): string {
			const ymd = [
				date.getFullYear(),
				pad2(date.getMonth() + 1),
				pad2(date.getDate()),
			].join("-");
			const hms = [
				pad2(date.getHours()),
				pad2(date.getMinutes()),
				pad2(date.getSeconds()),
			].join("_");
			return `${ymd} ${hms}_${siteName}_${objName}`;
		},
		// Reorder [east, north, z] -> [north, east, z] for LandXML (Northing first).
		// geometry vertices are already site coordinates (east/north); no CRS step.
		toNorthFirstFlat(vertices: number[]): number[] {
			const out: number[] = new Array(vertices.length);
			for (let i = 0; i < vertices.length; i += 3) {
				out[i] = vertices[i + 1];
				out[i + 1] = vertices[i];
				out[i + 2] = vertices[i + 2];
			}
			return out;
		},
		async runLandXMLExport(
			geometry: MeshGeometry,
			fileName: string | undefined,
		): Promise<string | undefined> {
			const site = this.$store.state.site;
			const landxmlPoints = this.toNorthFirstFlat(geometry.vertices);
			return LandXML.exportMesh(
				landxmlPoints,
				geometry.indices,
				site.site_name,
				fileName,
				this.obj.name,
			);
		},
		async generateLandXMLString(
			geometry: MeshGeometry,
		): Promise<string | undefined> {
			return this.runLandXMLExport(geometry, undefined);
		},
		async exportLandXML(geometry: MeshGeometry): Promise<void> {
			const site = this.$store.state.site;
			const fileName = this.createFileName(
				new Date(),
				this.obj.name,
				site.site_name,
			);
			await this.runLandXMLExport(geometry, fileName);
		},
		async requestDXFBlob(geometry: MeshGeometry): Promise<Blob> {
			// geometry vertices are already [east, north, z] in metres; pass through as-is.
			return fetchObjectDxfBlob({
				siteId: this.$store.state.site.site_uuid,
				name: this.obj.name,
				objectType: ObjectType.FOUNDATION_EXCAVATION,
				vertices: geometry.vertices,
				indices: geometry.indices,
				distanceUnit: distanceUnits.METERS,
			});
		},
		async generateDXFString(geometry: MeshGeometry): Promise<string> {
			const blob = await this.requestDXFBlob(geometry);
			return await blob.text();
		},
		async exportDXF(geometry: MeshGeometry): Promise<void> {
			const site = this.$store.state.site;
			const data = await this.requestDXFBlob(geometry);
			const blob = new Blob([data], { type: "application/dxf" });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = `${url}`;
			link.download = `${this.createFileName(new Date(), this.obj.name, site.site_name)}.dxf`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		},
		async uploadToDashboard(
			fileType: "xml" | "dxf",
			geometry: MeshGeometry,
		): Promise<void> {
			const site = this.$store.state.site;
			try {
				const fileData =
					fileType === "xml"
						? await this.generateLandXMLString(geometry)
						: await this.generateDXFString(geometry);
				const fileName = this.createFileName(
					new Date(),
					this.obj.name,
					site.site_name,
				);

				await uploadDesignFileToDashboard({
					fileType,
					fileData: fileData ?? "",
					fileName,
					siteId: site.site_uuid,
				});
				handleSingleUploadSuccess(this, this.$t("Registertodashboard"), {
					customMessage: `${fileType.toUpperCase()} ${this.$t("Registertodashboard")} ${this.$t("successful")}`,
				});
			} catch (e) {
				handleCustomError(
					this,
					e,
					`${fileType.toUpperCase()} ${this.$t("Registertodashboard")} ${this.$t("failed")}`,
				);
			}
		},
		async onClickExport(): Promise<void> {
			if (!this.obj) {
				handleCustomError(
					this,
					new Error("Object has been deleted"),
					this.$t("EXPORT_AS"),
				);
				this.closeDialog();
				return;
			}
			this.$store.dispatch("updateIsLoading", true);
			let landxmlGeometry: MeshGeometry | null = null;
			let dxfGeometry: MeshGeometry | null = null;
			try {
				const prep: Promise<void>[] = [];
				if (this.formats.landxml) {
					prep.push(
						this.obj.getGeometry().then((g) => {
							landxmlGeometry = g;
						}),
					);
				}
				if (this.formats.dxf) {
					prep.push(
						Promise.resolve().then(() => {
							dxfGeometry = this.obj.getPlanViewGeometry();
						}),
					);
				}
				await Promise.all(prep);
			} catch (e) {
				handleCustomError(
					this,
					e instanceof Error ? e : new Error(String(e)),
					this.$t("EXPORT_AS"),
				);
				this.$store.dispatch("updateIsLoading", false);
				this.closeDialog();
				return;
			}
			const tasks: Promise<unknown>[] = [];
			if (this.formats.landxml && landxmlGeometry) {
				const geom = landxmlGeometry;
				if (this.destination === "local") {
					tasks.push(
						this.exportLandXML(geom)
							.then(() => {
								handleSingleUploadSuccess(this, "LandXML", {
									customMessage: `LandXML ${this.$t("EXPORT_AS")} ${this.$t("successful")}`,
								});
							})
							.catch((e: unknown) => {
								handleCustomError(
									this,
									e,
									`LandXML ${this.$t("EXPORT_AS")} ${this.$t("failed")}`,
								);
								throw e;
							}),
					);
				} else {
					tasks.push(this.uploadToDashboard("xml", geom));
				}
			}
			if (this.formats.dxf && dxfGeometry) {
				const geom = dxfGeometry;
				if (this.destination === "local") {
					tasks.push(
						this.exportDXF(geom)
							.then(() => {
								handleSingleUploadSuccess(this, "DXF", {
									customMessage: `DXF ${this.$t("EXPORT_AS")} ${this.$t("successful")}`,
								});
							})
							.catch((e: unknown) => {
								handleCustomError(
									this,
									e,
									`DXF ${this.$t("EXPORT_AS")} ${this.$t("failed")}`,
								);
								throw e;
							}),
					);
				} else {
					tasks.push(this.uploadToDashboard("dxf", geom));
				}
			}
			try {
				await Promise.allSettled(tasks);
			} finally {
				this.$store.dispatch("updateIsLoading", false);
				this.closeDialog();
			}
		},
	},
});
</script>

<style scoped>
.format-checkboxes {
	padding-left: 4px;
}
</style>
